import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "@tanstack/react-router";
import aPrev from "../../../assets/img/arrow-back.png";
import aNext from "../../../assets/img/arrow-forw.png";
import {ipfs2md5} from "../../../utils";
import { imgErr } from "../../../components/imgErrHandler/imgErrHandler";
import './CardImg.css';

/**
 * CardImg component.
 *
 * Displays the NFT image with navigation controls (previous/next).
 * Features:
 * - Smooth cross-fade transition between image changes
 * - Previous/next navigation arrows
 * - Jump-to-number input field
 * - IPFS image conversion via ipfs2md5
 * - Error handling with imgErr fallback
 *
 * @param {Object} props - Component props
 * @param {string} props.img - Current image URL (IPFS or HTTP)
 * @param {string|number} props.cpos - Current token position (unused)
 * @param {string|number} props.npos - Next token ID for navigation
 * @param {string|number|null} props.ppos - Previous token ID for navigation
 * @param {Function} props.setItem - Setter for current item ID
 * @param {Object} props.tokInfo - Token metadata for error handling
 * @param {string} props.className - CSS class name for container
 * @returns {React.ReactElement} Image container with navigation
 */
const CardImg = ({ img, cpos, npos, ppos, setItem, tokInfo, className }) => {
    const [topImageSrc, setTopImageSrc] = useState(img);
    const [bottomImageSrc, setBottomImageSrc] = useState(img);
    const [isFading, setIsFading] = useState(false);

    img = ipfs2md5(img, true)+".jpg";

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (img !== topImageSrc) {
            setBottomImageSrc(img);
        }
    }, [img, topImageSrc]);

    const handleBottomImageLoad = () => {
        setIsFading(true);
        setTimeout(() => {
            setTopImageSrc(bottomImageSrc);
            setIsFading(false);
        }, 400);
    };

    let ni, pi, path, pp, np;
    // Extract the base path up to the last '/'
    const lastSlashIndex = location.pathname.lastIndexOf('/');
    const basePath = location.pathname.substring(0, lastSlashIndex + 1);
    path = basePath;

    // Construct prev and next URLs
    if (ppos != null) {
        pp = `${basePath}${ppos}`;
    }
    if (npos != null) {
        np = `${basePath}${npos}`;
    }

    if (npos) {
        ni = <Link id="nextImg" to={np} className="next-link" title="Next">
            <img alt=">" src={aNext} />
        </Link>;
    }
    if (ppos) {
        pi = <Link to={pp} className="prev-link" title="Previous">
            <img alt="<" src={aPrev} />
        </Link>;
    }

    const stableIdTop = topImageSrc ? `img-top-${btoa(topImageSrc)+Math.random().toString()}` : '';
    const stableIdBottom = bottomImageSrc ? `img-bottom-${btoa(bottomImageSrc)+Math.random().toString()}` : '';

    return (
        <div className={className}>
            {/* Top image (visible, fades out) */}
            {topImageSrc && (
                <img
                    src={topImageSrc}
                    id={stableIdTop}
                    className={`card-image ${isFading ? 'fade-out' : ''}`}
                    alt="NFT"
                    onError={(e) => imgErr(e, tokInfo)}
                />
            )}

            {/* Bottom image (hidden, loads new image) */}
            {bottomImageSrc && topImageSrc !== bottomImageSrc && (
                <img
                    src={bottomImageSrc}
                    id={stableIdBottom}
                    className="card-image"
                    style={{ opacity: 0 }}
                    alt="NFT"
                    onLoad={handleBottomImageLoad}
                    onError={(e) => imgErr(e, tokInfo)}
                />
            )}
            
            {pi}{ni}

            <div style={{ position: "absolute", bottom: "0em", padding: "0.5em", width: "95%" }}>
                <input
                    className={"input-purple"}
                    style={{ width: "25%" }}
                    type="number"
                    placeholder="Jump to #"
                    onBlur={(e) => {
                        navigate({ to: path + e.target.value });
                        setItem(e.target.value);
                    }}
                />
                &nbsp;&nbsp;
                <button className={"input-purple"}>Go</button>
                &nbsp;&nbsp;
                <div style={{ display: "inline-block" }} id="isForSale">&nbsp;</div>
            </div>
        </div>
    );
};

export default React.memo(CardImg);
